import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

if (import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
  setOptions({ key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY });
}

async function autocompletePlace(input: string, { signal }: { signal?: AbortSignal } = {}) {
  if (!import.meta.env.VITE_GOOGLE_MAPS_API_KEY) throw new TypeError("Google Maps API key is not configured");
  const { AutocompleteSuggestion } = await importLibrary("places");
  if (signal?.aborted) return [];
  const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({ input });
  return suggestions
    .map((item) => item.placePrediction)
    .filter(
      (prediction): prediction is google.maps.places.PlacePrediction => prediction !== null && !!prediction.placeId,
    );
}

type LocationQueryOptions<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn" | "enabled"> & {
  enabled?: boolean;
};

export function useLocationSearch(
  query: string,
  {
    query: { enabled: enabledOption, ...options } = {},
  }: { query?: LocationQueryOptions<google.maps.places.PlacePrediction[]> } = {},
) {
  return useQuery({
    queryKey: ["useLocationSearch", query],
    queryFn: ({ signal }) => autocompletePlace(query, { signal }),
    ...options,
    enabled: !!import.meta.env.VITE_GOOGLE_MAPS_API_KEY && !!query && (enabledOption ?? true),
  });
}

async function lookupPlace(placeId: string, { signal }: { signal?: AbortSignal } = {}) {
  if (!import.meta.env.VITE_GOOGLE_MAPS_API_KEY) throw new TypeError("Google Maps API key is not configured");
  const { Place } = await importLibrary("places");
  if (signal?.aborted) return undefined;
  const { place } = await new Place({ id: placeId }).fetchFields({
    fields: ["displayName", "location", "googleMapsURI"],
  });
  return place;
}

export function useLookupPlace(
  placeId: string | null | undefined,
  { query: options = {} }: { query?: LocationQueryOptions<google.maps.places.Place | null | undefined> } = {},
) {
  return useQuery({
    queryKey: ["useLookupPlace", placeId],
    queryFn: ({ signal }) => lookupPlace(placeId!, { signal }),
    ...options,
    enabled: !!placeId && !!import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });
}
